import { damageReportsApi, type DamageReportUrgency } from '../../services/damage-reports.api';
import { tenantWelcomePagesApi } from '../../services/tenant-welcome-pages.api';
import type { TenantWelcomePagePublicDto } from '../../services/tenant-welcome-pages.api';
import { t } from '../../services/i18n';

type State = 'loading' | 'ready' | 'not-found' | 'error';

function esc(value: string): string {
  const span = document.createElement('span');
  span.textContent = value;
  return span.innerHTML;
}

function renderMultiline(value?: string | null): string {
  if (!value) return '';
  return esc(value).replace(/\n/g, '<br>');
}

function isDirectBrowserUploadUrl(uploadUrl: string): boolean {
  return /^https?:\/\//i.test(uploadUrl) || uploadUrl.startsWith('/');
}

export class RcTenantWelcomePage extends HTMLElement {
  private state: State = 'loading';
  private token: string | null = null;
  private welcomePage: TenantWelcomePagePublicDto | null = null;
  private isSubmitting = false;
  private submitError = '';
  private submitSuccess = false;

  connectedCallback(): void {
    this.token = new URLSearchParams(window.location.search).get('token');
    this.addEventListener('submit', this.handleSubmit);
    this.render();
    void this.loadPage();
  }

  disconnectedCallback(): void {
    this.removeEventListener('submit', this.handleSubmit);
  }

  private async loadPage(): Promise<void> {
    if (!this.token) {
      this.state = 'not-found';
      this.render();
      return;
    }

    try {
      const welcomePage = await tenantWelcomePagesApi.getByToken(this.token);
      if (!welcomePage) {
        this.state = 'not-found';
      } else {
        this.welcomePage = welcomePage;
        this.state = 'ready';
      }
    } catch {
      this.state = 'error';
    }

    this.render();
  }

  private render(): void {
    this.innerHTML = `
      <section class="public-welcome-shell">
        <div class="public-welcome-card">
          ${this.renderContent()}
        </div>
      </section>`;
  }

  private renderContent(): string {
    if (this.state === 'loading') {
      return `<p class="public-welcome-loading">${t('tenantWelcome.public.loading')}</p>`;
    }

    if (this.state === 'not-found') {
      return `
        <div class="public-welcome-empty">
          <h1>${t('tenantWelcome.public.notFoundTitle')}</h1>
          <p>${t('tenantWelcome.public.notFoundBody')}</p>
        </div>`;
    }

    if (this.state === 'error') {
      return `
        <div class="public-welcome-empty">
          <h1>${t('tenantWelcome.public.errorTitle')}</h1>
          <p>${t('tenantWelcome.public.errorBody')}</p>
        </div>`;
    }

    const page = this.welcomePage!;
    const wifiBlock = page.wifiName || page.wifiPassword
      ? `<div class="public-welcome-tile">
          <h2>${t('tenantWelcome.public.wifiTitle')}</h2>
          ${page.wifiName ? `<p><strong>${t('tenantWelcome.wifiName')}:</strong> ${esc(page.wifiName)}</p>` : ''}
          ${page.wifiPassword ? `<p><strong>${t('tenantWelcome.wifiPassword')}:</strong> ${esc(page.wifiPassword)}</p>` : ''}
        </div>`
      : '';

    const contactsBlock = page.emergencyContacts.length > 0
      ? `<div class="public-welcome-section">
          <h2>${t('tenantWelcome.contactsTitle')}</h2>
          <div class="public-welcome-contacts">
            ${page.emergencyContacts.map((contact) => `
              <div class="public-welcome-contact">
                <strong>${esc(contact.label)}</strong>
                ${contact.name ? `<span>${esc(contact.name)}</span>` : ''}
                ${contact.phone ? `<a href="tel:${esc(contact.phone)}">${esc(contact.phone)}</a>` : ''}
              </div>
            `).join('')}
          </div>
        </div>`
      : '';

    return `
      <div class="public-welcome-hero">
        <p class="public-welcome-eyebrow">${esc(page.landlordDisplayName)}${page.propertyAddress ? ` · ${esc(page.propertyAddress)}` : ''}</p>
        <h1>${esc(page.pageTitle)}</h1>
        ${page.introduction ? `<p class="public-welcome-intro">${renderMultiline(page.introduction)}</p>` : ''}
      </div>
      <div class="public-welcome-grid">
        ${wifiBlock}
        ${page.trashCollectionInfo ? `<div class="public-welcome-tile"><h2>${t('tenantWelcome.trashCollectionInfo')}</h2><p>${renderMultiline(page.trashCollectionInfo)}</p></div>` : ''}
        ${page.parkingInfo ? `<div class="public-welcome-tile"><h2>${t('tenantWelcome.parkingInfo')}</h2><p>${renderMultiline(page.parkingInfo)}</p></div>` : ''}
      </div>
      ${this.renderDamageReportSection()}
      ${page.houseRules ? `<div class="public-welcome-section"><h2>${t('tenantWelcome.houseRules')}</h2><p>${renderMultiline(page.houseRules)}</p></div>` : ''}
      ${contactsBlock}
      ${page.additionalInfo ? `<div class="public-welcome-section"><h2>${t('tenantWelcome.additionalInfo')}</h2><p>${renderMultiline(page.additionalInfo)}</p></div>` : ''}
    `;
  }

  private renderDamageReportSection(): string {
    return `
      <section class="public-welcome-section public-welcome-report-section">
        <div class="public-welcome-report-head">
          <div>
            <h2>${t('tenantWelcome.public.damageReport.title')}</h2>
            <p>${t('tenantWelcome.public.damageReport.helper')}</p>
          </div>
          <span class="public-welcome-report-pill">${t('tenantWelcome.public.damageReport.noLogin')}</span>
        </div>
        <form class="public-welcome-report-form">
          <label>
            <span>${t('tenantWelcome.public.damageReport.descriptionLabel')}</span>
            <textarea name="description" rows="4" maxlength="4000" required placeholder="${esc(t('tenantWelcome.public.damageReport.descriptionPlaceholder'))}"></textarea>
          </label>
          <div class="public-welcome-report-grid">
            <label>
              <span>${t('tenantWelcome.public.damageReport.urgencyLabel')}</span>
              <select name="urgency">
                ${this.renderUrgencyOption('Low')}
                ${this.renderUrgencyOption('Normal', true)}
                ${this.renderUrgencyOption('High')}
                ${this.renderUrgencyOption('Urgent')}
              </select>
            </label>
            <label>
              <span>${t('tenantWelcome.public.damageReport.photosLabel')}</span>
              <input type="file" name="photos" accept="image/*" multiple>
            </label>
          </div>
          ${this.submitError ? `<div class="form-error">${esc(this.submitError)}</div>` : ''}
          ${this.submitSuccess ? `<div class="form-success">${esc(t('tenantWelcome.public.damageReport.success'))}</div>` : ''}
          <div class="public-welcome-report-actions">
            <button class="button button-primary" type="submit" ${this.isSubmitting ? 'disabled' : ''}>${this.isSubmitting ? t('common.uploading') : t('tenantWelcome.public.damageReport.submit')}</button>
          </div>
        </form>
      </section>
    `;
  }

  private renderUrgencyOption(value: DamageReportUrgency, selected = false): string {
    return `<option value="${value}" ${selected ? 'selected' : ''}>${esc(t(`damageReports.urgency.${value.toLowerCase()}`))}</option>`;
  }

  private handleSubmit = async (event: Event): Promise<void> => {
    const form = event.target as HTMLFormElement | null;
    if (!form || !this.token) {
      return;
    }

    event.preventDefault();
    const formData = new FormData(form);
    const description = (formData.get('description') || '').toString().trim();
    const urgency = ((formData.get('urgency') || 'Normal').toString() || 'Normal') as DamageReportUrgency;
    const fileInput = form.querySelector<HTMLInputElement>('input[name="photos"]');
    const files = Array.from(fileInput?.files || []);

    this.isSubmitting = true;
    this.submitError = '';
    this.submitSuccess = false;
    this.render();

    try {
      const report = await damageReportsApi.createForWelcomePage(this.token, { description, urgency });

      for (const file of files) {
        const upload = await damageReportsApi.createPublicPhotoUpload(this.token, report.id, {
          fileName: file.name,
          contentType: file.type || 'image/jpeg',
          fileSizeBytes: file.size,
        });

        if (isDirectBrowserUploadUrl(upload.uploadUrl)) {
          const response = await fetch(upload.uploadUrl, {
            method: 'PUT',
            headers: { 'Content-Type': file.type || 'image/jpeg' },
            body: file,
          });

          if (!response.ok) {
            throw new Error(t('document.uploadError'));
          }
        } else {
          await damageReportsApi.uploadPublicPhotoContent(this.token, report.id, upload.photoId, file);
        }
      }

      form.reset();
      this.submitSuccess = true;
    } catch (error: any) {
      this.submitError = error?.message || t('tenantWelcome.public.damageReport.submitError');
    } finally {
      this.isSubmitting = false;
      this.render();
    }
  };
}

if (!customElements.get('rc-tenant-welcome-page')) {
  customElements.define('rc-tenant-welcome-page', RcTenantWelcomePage);
}
